import { default as _log } from '@dw/loglevel';
import Query from './query.js';

// lodash
import find from 'lodash-es/find';
import difference from 'lodash-es/difference';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import get from 'lodash-es/get';

/**
 * ## `snapshot` event value.
 * - Deleted documents will be available as `null` value in the Snapshot value.
 * - Not Found documents will also be available as `null` value, but that's published only
 *   after server-sync is completed. So, if document doesn't exist in the local database
 *   it's initial value will become ready only when server-sync is completed.
 */
class QueryByIds extends Query {

  constructor({ couchdb, database, queryId, docIds, localOnly, once, dispatch }) {
    super({ couchdb, database, queryId, localOnly, once, dispatch });

    this.docIds = docIds;
    this._onLocalDbChanges = this._onLocalDbChanges.bind(this);
    this.logPrefix = `dreamDB.couchDB.${this.couchdb.name}.db.${this.database.dbName}.QueryByIds.${this.queryId}`;
    //set logger
    this.log = _log.getLogger(`dreamdb.queryByIds.${this.couchdb.name}.${this.database.dbName}.${this.queryId}`);
    this._dispatchQuery();
    this._startQuery();
  }

  /**
   * Returns serialized format of the query. e.g. `{id:"", type:"BY_ID", docId: ""}`
   */
  get query() {
    return {
      id: this.queryId,
      type: 'BY_IDS',
      docIds: this.docIds,
      localOnly: this.localOnly,
      once: this.once
    };
  }

  /**
   * This method starts reading query in local database and on remote database.
   */
  async _startQuery() {
    this._setMetadata({ lastQueryTime: new Date().getTime() });

    if (this.once) {
      this._readDocumentOnce();
      return;
    }

    this._readLocalDocuments();
    if (!this.localOnly) {
      this._readRemoteDocuments();
    }
  }

  /**
   * It performs first query on remote database and after performs on local database.If once=true, then it will 
   * perform query only on local database.
   */
  async _readDocumentOnce() {
    //Perform only on local database
    if (this.localOnly) {
      await this._performLocalQuery();
      this._resolveVal(this.value);
      this._setMetadata({ localInSync: true });
      return;
    }

    //Perform on both remote database and local database
    let response = await this.database.remoteDb.queryByIds({ queryId: this.queryId, docIds: this.docIds, once: this.once });
    if (response.success) {
      await this._performLocalQuery();
      this._resolveVal(this.value);
      this._setMetadata({ localInSync: true, inSync: true });
      return;
    }

    this.log.error(`${this.logPrefix}._readDocumentOnce(): once query operation failed::`, response.error);
    this._rejectVal(response.error);
    this._setMetadata({ localInSync: true, inSync: true, error: response.error });
  }

  /**
   * It fetches the document from the local db and set value
   */
  async _performLocalQuery() {
    let response = await this._fetchLocalDocuments();
    this.log.debug(`${this.logPrefix}._performLocalQuery(): documents are read from localDb ::`, response);
    let value = this._parseQuery(response);

    this._setNullValues(value);

    if (!isEqual(this.value, value)) {
      this._setValue(value);
    }
  }

  /**
   * It parses the query result. It sets _id, _rev and _deleted fields for deleted
   * documents.
   * @param {Object} response query response
   * @return {Object} Return Object. key=docId and value=document
   */
  _parseQuery(response) {
    if(!response) {
      return;
    }
    
    let value = {};

    response.forEach((row) => {
      if (row.error === "not_found") {
        return;
      }

      if(row.value.deleted) {
        value[row.id] = {
          _id: row.id,
          _rev: row.value.rev,
          _deleted: true
        };
        return;
      }

      value[row.id] = row.doc;
    });

    return value;
  }

  /**
   * This method is internal method used by `_startQuery` method.
   * This method read query from remote database.
   */
  async _readRemoteDocuments() {
    let response = await this.database.remoteDb.queryByIds({ queryId: this.queryId, docIds: this.docIds, once: this.once });
    if (response.success) {
      let value = {};

      if (this.value === undefined) {
        // When documents not found from local database then we re-runs the query in local database after remote query
        // is completed to prevents extra `null` value snapshot.
        await this._performLocalQuery();
      }

      // set null values for not-found documents
      value = { ...this.value };
      this._setNullValues(value);

      if (!isEqual(value, this.value)) {
        this._setValue({ ...this.value, ...value });
      }

      this._setMetadata({ inSync: true });
      this._resolveVal(this.value);
      return;
    }

    this._rejectVal(response.error);
    this.log.error(`${this.logPrefix}._readRemoteDocuments(): remote Query operation failed::`, response.error);
    this._setMetadata({ inSync: true, error: response.error });
  }

  /**
   * This method reads documents from the local database.
   * Listens `changes` feed of the localDb
   *  - If record is not found, do nothing
   *  - If record is deleted, set _id, _rev and _deleted fields.
   *  - If record is found, set value.
   */
  async _readLocalDocuments() {
    let response = await this._fetchLocalDocuments();

    //Starts watching changes feed
    this._watchLocalDbChanges();

    let value = this._parseQuery(response);

    if (isEmpty(value)) {
      this.log.debug(`${this.logPrefix}._readLocalDocuments(): No any documents found from local database.`);
      if (this.localOnly) {
        //set null values for not found docs
        this._setNullValues(value);
        this._setValue(value);
        this._resolveVal(this.value);
      }
      this._setMetadata({ localInSync: true });
      return;
    }

    this.log.info(`${this.logPrefix}._readLocalDocuments(): Read document from local completed`, value);

    if (this.localOnly) {
      //set null values for not found docs
      this._setNullValues(value);
      this._setValue(value);
      this._resolveVal(this.value);
      this._setMetadata({ localInSync: true });
      return;
    }

    this._setValue(value);
    this._setMetadata({ localInSync: true });
  }

  /**
   * It fetches document from the local database.
   * @returns {Promise} Returns promise which resolves with query result fetched
   */
  async _fetchLocalDocuments() {
    return await this.database.localDb.fetch(this.docIds);
  }

  /**
   * This method called on localDb `changes` event. Updates value if requested docs are changed
   * @param {Array} Array of changed documents in local database
   */
  _onLocalDbChanges(docs) {
    let changedDocs = {};

    docs.forEach((doc) => {
      let index = this.docIds.indexOf(doc.id);

      if (index === -1) {
        return;
      }

      if((!this.value && doc.deleted) || (this.value && !this.value[doc.id] && doc.deleted)) {
        changedDocs[doc.id] = {
          _id: doc.doc._id,
          _rev: doc.doc._rev,
          _deleted: true
        };
        return;
      }

      changedDocs[doc.id] = doc.doc;
    });

    if (Object.keys(changedDocs).length) {
      this.log.info(`${this.logPrefix}._onLocalDbChanges(): document(s) is/are updated in local db`, docs);

      if (this._isValueChanged(changedDocs)) {
        this._setValue({ ...this.value, ...changedDocs });
      }
    }

  }

  /**
   * It compares changedDocs with current value and returns true if docs are actually changed, false otherwise
   * @param {Object} changedDocs 
   * @return {boolean} true or false
   */
  _isValueChanged(changedDocs) {
    let doc = find(changedDocs, (value, key) => {
      let currentDoc = this.value && this.value[key];

      return get(value, '_rev') !== get(currentDoc, '_rev');
    });

    return doc ? true : false;
  }

  /**
   * It sets null values for docIds which are not found.
   * @param {Object} value 
   */
  _setNullValues(value) {
    this.docIds.forEach(id => {
      if (value[id] === undefined) {
        value[id] = null;
      }
    });
  }

  /**
   * - removed docs will be removed from the `value` hash.
   * - For new docs a query is performed on the localDb and those are populated in the value.
   * - If any change is found in the `value`, then only `snapshot` event is dispatched. 
   */
  async _updateLocalQuery(prevDocIds, docIds) {

    //Identify removedIds and addedIds
    let removedDocIds = difference(prevDocIds, docIds);
    let addedDocIds = difference(docIds, prevDocIds);

    this.log.info(`${this.logPrefix}._updateLocalQuery(): RemovedIds: ${removedDocIds} and addedIds: ${addedDocIds}`);

    //Returns if not any of ids added or removed
    if (!addedDocIds.length && !removedDocIds.length) {
      return;
    }

    //Removed docs will be removed from the `value` hash.
    removedDocIds.forEach((id) => {
      delete this.value[id];
    });

    //Read again 
    this._readLocalDocuments();
  }

  /**
   * An intenal method used by update method.
   * It will update remote query.
   */
  async _updateRemoteQuery() {
    let response = await this.database.remoteDb.updateQueryByIds({ queryId: this.queryId, docIds: this.docIds });

    if (response.success) {
      let value = {};
      if (isEmpty(this.value)) {
        // When documents not found from local database then we re-runs the query in local database after remote query
        // is completed to prevents extra `null` value snapshot.
        let localResponse = await this._performLocalQuery();
        value = this._parseQuery(localResponse);

        if (isEmpty(this.value)) {
          this._setValue(value);
        }
      }

      value = { ...this.value };
      this._setNullValues(value);

      if (this._isValueChanged(value)) {
        this._setValue({ ...this.value, ...value });
      }
      this._setMetadata({ inSync: true });
      return;
    }

    this.log.error(`${this.logPrefix}._updateRemoteQuery(): update remote Query operation failed::`, response.error);
    this._setMetadata({ inSync: true, error: response.error });
  }

  /**
   * It's used for update docIds in current query.
   * It's no-op is docIds is not updated. (not added or removed)
   * It will throw error if method is called for `once=true` query.
   * @param {Array} docIds 
   */
  update(docIds) {
    if (this.once) {
      throw `Can't update once query`;
    }

    if (!Array.isArray(docIds)) {
      this.log.error(`${this.logPrefix}.update(): docIds should be type of Array.`);
      return;
    }

    if (!docIds.length) {
      this.log.error(`${this.logPrefix}.update(): atleast one docId is required to update query.`);
      return;
    }

    let isArrayOfStr = docIds.every((id) => {
      return typeof id === 'string';
    });

    if (!isArrayOfStr) {
      this.log.error(`${this.logPrefix}.update(): docIds should be array of string`, docIds);
      return;
    }

    let prevDocIds = this.docIds;
    this.docIds = docIds;
    this._dispatchQueryUpdate();
    this._setMetadata({ lastQueryTime: new Date().getTime(), localInSync: false, inSync: false });
    this._updateLocalQuery(prevDocIds, docIds);

    if (!this.localOnly) {
      this._updateRemoteQuery();
    }
  }

}

export default QueryByIds;
